import { create } from 'zustand';
import type { FeedItem, SearchResult, TrendingTag, User } from '@/types';
import { socialService } from '@/services/socialService';
import toast from 'react-hot-toast';

interface SocialState {
  feed: FeedItem[];
  trending: TrendingTag[];
  following: User[];
  searchResults: SearchResult[];
  searchQuery: string;
  recentSearches: string[];
  feedCursor: string | null;
  hasFeedMore: boolean;
  isLoadingFeed: boolean;
  isLoadingSearch: boolean;

  fetchFeed: (reset?: boolean) => Promise<void>;
  fetchTrending: () => Promise<void>;
  like: (feedItemId: string) => Promise<void>;
  unlike: (feedItemId: string) => Promise<void>;
  comment: (feedItemId: string, text: string) => Promise<void>;
  save: (feedItemId: string) => Promise<void>;
  unsave: (feedItemId: string) => Promise<void>;
  follow: (userId: string) => Promise<void>;
  unfollow: (userId: string) => Promise<void>;
  search: (query: string) => Promise<void>;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  addRecentSearch: (query: string) => void;
}

export const useSocialStore = create<SocialState>((set, get) => ({
  feed: [],
  trending: [],
  following: [],
  searchResults: [],
  searchQuery: '',
  recentSearches: JSON.parse(
    localStorage.getItem('vestir-recent-searches') ?? '[]',
  ),
  feedCursor: null,
  hasFeedMore: true,
  isLoadingFeed: false,
  isLoadingSearch: false,

  fetchFeed: async (reset = false) => {
    const { feedCursor, feed } = get();
    const cursor = reset ? undefined : (feedCursor ?? undefined);

    set({ isLoadingFeed: true });
    try {
      const response = await socialService.getFeed(cursor);
      const newFeed = reset ? response.items : [...feed, ...response.items];
      set({
        feed: newFeed,
        hasFeedMore: response.hasMore,
        feedCursor: response.nextCursor,
        isLoadingFeed: false,
      });
    } catch {
      set({ isLoadingFeed: false });
      toast.error('Failed to load feed');
    }
  },

  fetchTrending: async () => {
    try {
      const trending = await socialService.getTrending();
      set({ trending });
    } catch {
      /* silent */
    }
  },

  like: async (feedItemId) => {
    set((state) => ({
      feed: state.feed.map((item) =>
        item.id === feedItemId
          ? { ...item, isLiked: true, likes: item.likes + 1 }
          : item,
      ),
    }));
    try {
      await socialService.like(feedItemId);
    } catch {
      set((state) => ({
        feed: state.feed.map((item) =>
          item.id === feedItemId
            ? { ...item, isLiked: false, likes: item.likes - 1 }
            : item,
        ),
      }));
    }
  },

  unlike: async (feedItemId) => {
    set((state) => ({
      feed: state.feed.map((item) =>
        item.id === feedItemId
          ? { ...item, isLiked: false, likes: Math.max(0, item.likes - 1) }
          : item,
      ),
    }));
    try {
      await socialService.unlike(feedItemId);
    } catch {
      set((state) => ({
        feed: state.feed.map((item) =>
          item.id === feedItemId
            ? { ...item, isLiked: true, likes: item.likes + 1 }
            : item,
        ),
      }));
    }
  },

  comment: async (feedItemId, text) => {
    try {
      await socialService.comment(feedItemId, text);
      set((state) => ({
        feed: state.feed.map((item) =>
          item.id === feedItemId
            ? { ...item, commentsCount: item.commentsCount + 1 }
            : item,
        ),
      }));
      toast.success('Comment posted');
    } catch {
      toast.error('Failed to post comment');
    }
  },

  save: async (feedItemId) => {
    set((state) => ({
      feed: state.feed.map((item) =>
        item.id === feedItemId ? { ...item, isSaved: true } : item,
      ),
    }));
    try {
      await socialService.save(feedItemId);
      toast.success('Saved!');
    } catch {
      set((state) => ({
        feed: state.feed.map((item) =>
          item.id === feedItemId ? { ...item, isSaved: false } : item,
        ),
      }));
    }
  },

  unsave: async (feedItemId) => {
    set((state) => ({
      feed: state.feed.map((item) =>
        item.id === feedItemId ? { ...item, isSaved: false } : item,
      ),
    }));
    try {
      await socialService.unsave(feedItemId);
    } catch {
      set((state) => ({
        feed: state.feed.map((item) =>
          item.id === feedItemId ? { ...item, isSaved: true } : item,
        ),
      }));
    }
  },

  follow: async (userId) => {
    try {
      await socialService.follow(userId);
      toast.success('Following!');
    } catch {
      toast.error('Failed to follow');
    }
  },

  unfollow: async (userId) => {
    try {
      await socialService.unfollow(userId);
    } catch {
      toast.error('Failed to unfollow');
    }
  },

  search: async (query) => {
    if (!query.trim()) {
      set({ searchResults: [] });
      return;
    }
    set({ isLoadingSearch: true, searchQuery: query });
    try {
      const results = await socialService.search(query);
      set({ searchResults: results, isLoadingSearch: false });
      get().addRecentSearch(query);
    } catch {
      set({ isLoadingSearch: false });
    }
  },

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  clearSearch: () => set({ searchResults: [], searchQuery: '' }),

  addRecentSearch: (query) => {
    const { recentSearches } = get();
    const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(
      0,
      10,
    );
    localStorage.setItem('vestir-recent-searches', JSON.stringify(updated));
    set({ recentSearches: updated });
  },
}));
